import type { ReactNode } from "react";

const NAVY = "#0B1F33";
const CREAM = "#F6F1E7";
const TEAL = "#167C80";
const RED = "#B14C42";

type ChartFrameProps = {
  id: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  caption: ReactNode;
  source: ReactNode;
};

function ChartFrame({ id, title, subtitle, children, caption, source }: ChartFrameProps) {
  return (
    <figure className="not-prose my-10 overflow-hidden rounded-2xl border border-[#0B1F33]/15 bg-[#F6F1E7] shadow-sm" aria-labelledby={`${id}-title`}>
      <div className="border-b border-[#0B1F33]/15 px-5 py-5 sm:px-7">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0B1F33]/65">Data figure</p>
        <p id={`${id}-title`} className="mt-2 font-serif text-xl text-[#0B1F33] sm:text-2xl">{title}</p>
        {subtitle && <p className="mt-2 max-w-3xl text-sm leading-6 text-[#0B1F33]/75">{subtitle}</p>}
      </div>
      <div className="overflow-x-auto px-3 py-5 sm:px-6">{children}</div>
      <figcaption className="border-t border-[#0B1F33]/15 bg-white/45 px-5 py-4 text-sm leading-6 text-[#0B1F33]/80 sm:px-7">
        <p>{caption}</p>
        <p className="mt-2 text-xs leading-5 text-[#0B1F33]/65"><strong className="font-semibold text-[#0B1F33]">Source:</strong> {source}</p>
      </figcaption>
    </figure>
  );
}

const forecastBars = [
  { label: "FactSet median", value: 90, colour: NAVY },
  { label: "ABC", value: 84, colour: "#56728D" },
  { label: "MUFG", value: 90, colour: TEAL },
  { label: "Bloomberg median", value: 85, colour: "#7F9BA9" },
  { label: "Actual", value: 29, colour: RED },
] as const;

export function PayrollForecastFigure() {
  const max = 130;
  const baseline = 250;
  const chartHeight = 205;

  return (
    <ChartFrame
      id="payroll-forecast-figure"
      title="Forecasts, then the print"
      caption="Forecasts published before the print, and the Bureau of Labor Statistics figure. Not one poll."
      source={<><a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://insight.factset.com/total-nonfarm-payrolls-for-september-2026-are-projected-to-rise-by-90000" target="_blank" rel="noopener noreferrer">FactSet, 1 October 2026</a>; <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://abcnews.com/Business/jobs-report-show-hiring-stayed-resilient-september/story?id=136915472" target="_blank" rel="noopener noreferrer">ABC News, 2 October 2026</a>; <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://www.mufgresearch.com/macro/us-labor-update-october-1-2026/" target="_blank" rel="noopener noreferrer">MUFG, 1 October 2026</a>; <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://www.bls.gov/news.release/empsit.nr0.htm" target="_blank" rel="noopener noreferrer">US Bureau of Labor Statistics, 2 October 2026</a>.</>}
    >
      <svg className="mx-auto block min-w-[660px] max-w-full" viewBox="0 0 720 345" role="img" aria-labelledby="payroll-forecast-figure-title payroll-forecast-figure-desc">
        <title id="payroll-forecast-figure-title">Pre-release September payroll forecasts compared with the actual Bureau of Labor Statistics report</title>
        <desc id="payroll-forecast-figure-desc">FactSet median 90,000, ABC 84,000, MUFG 90,000, Bloomberg contributor median 85,000, and the BLS actual 29,000. FactSet range runs from 60,000 to 130,000.</desc>
        <rect x="0" y="0" width="720" height="345" fill={CREAM} />
        {[0, 30, 60, 90, 120, 130].map((tick) => {
          const y = baseline - (tick / max) * chartHeight;
          return <g key={tick}><line x1="55" x2="690" y1={y} y2={y} stroke={`${NAVY}22`} /><text x="43" y={y + 4} textAnchor="end" fill={`${NAVY}aa`} fontSize="12">{tick}k</text></g>;
        })}
        {forecastBars.map((bar, index) => {
          const x = 105 + index * 120;
          const height = (bar.value / max) * chartHeight;
          const y = baseline - height;
          return (
            <g key={bar.label}>
              <rect x={x} y={y} width="72" height={height} rx="5" fill={bar.colour} />
              <text x={x + 36} y={y - 10} textAnchor="middle" fill={NAVY} fontSize="16" fontWeight="700">{bar.value}k</text>
              <text x={x + 36} y="278" textAnchor="middle" fill={NAVY} fontSize="12" fontWeight="600">{bar.label}</text>
            </g>
          );
        })}
        <line x1="105" x2="177" y1={baseline - (60 / max) * chartHeight} y2={baseline - (60 / max) * chartHeight} stroke={NAVY} strokeWidth="2" />
        <line x1="105" x2="177" y1={baseline - (130 / max) * chartHeight} y2={baseline - (130 / max) * chartHeight} stroke={NAVY} strokeWidth="2" />
        <line x1="141" x2="141" y1={baseline - (130 / max) * chartHeight} y2={baseline - (60 / max) * chartHeight} stroke={NAVY} strokeWidth="2" strokeDasharray="3 3" />
        <text x="190" y="51" fill={NAVY} fontSize="12">FactSet’s range: 60k–130k (27 estimates)</text>
        <text x="55" y="325" fill={`${NAVY}aa`} fontSize="12">Nonfarm payroll change, thousands</text>
      </svg>
    </ChartFrame>
  );
}

const industryChanges = [
  ["Health care and social assistance", 23.0],
  ["Construction", 11],
  ["Leisure and hospitality", 10],
  ["Manufacturing", 9],
  ["Transportation and warehousing", 7.6],
  ["Other services", 6],
  ["Retail trade", 5.8],
  ["Wholesale trade", 5.0],
  ["Financial activities", -7],
  ["Professional and business services", -9],
  ["Information", -10],
  ["Government", -17],
  ["Total nonfarm", 29],
] as const;

export function IndustryChangeFigure() {
  const zeroX = 420;
  const scale = 9;
  return (
    <ChartFrame
      id="industry-change-figure"
      title="September 2026, over the month"
      subtitle="The bureau said employment in every major industry changed little."
      caption="Diverging monthly change by industry, in thousands. The chart is composition, not a league table of industries “exposed to AI”."
      source={<><a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://www.bls.gov/news.release/empsit.b.htm" target="_blank" rel="noopener noreferrer">US Bureau of Labor Statistics, Summary Table B, September 2026, preliminary</a>.</>}
    >
      <svg className="mx-auto block min-w-[790px] max-w-full" viewBox="0 0 850 560" role="img" aria-labelledby="industry-change-figure-title industry-change-figure-desc">
        <title id="industry-change-figure-title">September 2026 payroll change by selected industry, in thousands</title>
        <desc id="industry-change-figure-desc">Industry changes range from total nonfarm plus 29 thousand to government minus 17 thousand. Financial activities fell by 7 thousand.</desc>
        <rect width="850" height="560" fill={CREAM} />
        <line x1={zeroX} x2={zeroX} y1="50" y2="520" stroke={NAVY} strokeWidth="2" />
        {[-20, -10, 0, 10, 20, 30].map((tick) => <g key={tick}><line x1={zeroX + tick * scale} x2={zeroX + tick * scale} y1="50" y2="520" stroke={`${NAVY}16`} /><text x={zeroX + tick * scale} y="38" textAnchor="middle" fill={`${NAVY}aa`} fontSize="12">{tick > 0 ? `+${tick}` : tick}</text></g>)}
        {industryChanges.map(([label, value], index) => {
          const y = 63 + index * 35;
          const width = Math.abs(value) * scale;
          const x = value >= 0 ? zeroX : zeroX - width;
          const colour = value >= 0 ? TEAL : RED;
          const valueLabel = `${value > 0 ? "+" : ""}${value}`;
          return <g key={label}>
            <text x="390" y={y + 15} textAnchor="end" fill={NAVY} fontSize="12" fontWeight={label === "Total nonfarm" ? "700" : "500"}>{label}</text>
            <rect x={x} y={y} width={width} height="20" rx="3" fill={colour} />
            <text x={value >= 0 ? x + width + 7 : x - 7} y={y + 15} textAnchor={value >= 0 ? "start" : "end"} fill={NAVY} fontSize="12" fontWeight="700">{valueLabel}k</text>
          </g>;
        })}
        <text x="420" y="545" textAnchor="middle" fill={`${NAVY}aa`} fontSize="12">Change in employment, thousands</text>
      </svg>
    </ChartFrame>
  );
}

const outlookRows = [
  ["Claims adjusters, appraisers, examiners, and investigators", -6],
  ["Insurance underwriters", -4],
  ["Bookkeeping, accounting, and auditing clerks", -6],
  ["Accountants and auditors", 5],
] as const;

export function OccupationalOutlookFigure() {
  const zeroX = 475;
  const scale = 28;
  return (
    <ChartFrame
      id="occupational-outlook-figure"
      title="Decade outlook, not this year"
      caption="Projected employment change, 2025–35. This is not the past year’s change in claims jobs, and it is not a forecast from Integrated.Social."
      source={<><a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://www.bls.gov/ooh/business-and-financial/claims-adjusters-appraisers-examiners-and-investigators.htm" target="_blank" rel="noopener noreferrer">BLS Occupational Outlook Handbook: claims occupations</a>; <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://www.bls.gov/ooh/business-and-financial/insurance-underwriters.htm" target="_blank" rel="noopener noreferrer">insurance underwriters</a>; <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://www.bls.gov/ooh/office-and-administrative-support/bookkeeping-accounting-and-auditing-clerks.htm" target="_blank" rel="noopener noreferrer">bookkeeping clerks</a>; <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://www.bls.gov/ooh/business-and-financial/accountants-and-auditors.htm" target="_blank" rel="noopener noreferrer">accountants and auditors</a>.</>}
    >
      <svg className="mx-auto block min-w-[780px] max-w-full" viewBox="0 0 830 290" role="img" aria-labelledby="occupational-outlook-figure-title occupational-outlook-figure-desc">
        <title id="occupational-outlook-figure-title">BLS occupational outlook, 2025 to 2035</title>
        <desc id="occupational-outlook-figure-desc">The BLS projects claims occupations down 6 percent, insurance underwriters down 4 percent, bookkeeping clerks down 6 percent and accountants and auditors up 5 percent.</desc>
        <rect width="830" height="290" fill={CREAM} />
        <line x1={zeroX} x2={zeroX} y1="46" y2="250" stroke={NAVY} strokeWidth="2" />
        {[-6, -4, -2, 0, 2, 4, 6].map((tick) => <g key={tick}><line x1={zeroX + tick * scale} x2={zeroX + tick * scale} y1="46" y2="250" stroke={`${NAVY}16`} /><text x={zeroX + tick * scale} y="32" textAnchor="middle" fill={`${NAVY}aa`} fontSize="12">{tick > 0 ? `+${tick}%` : `${tick}%`}</text></g>)}
        {outlookRows.map(([label, value], index) => {
          const y = 62 + index * 45;
          const width = Math.abs(value) * scale;
          const x = value >= 0 ? zeroX : zeroX - width;
          return <g key={label}>
            <text x="448" y={y + 15} textAnchor="end" fill={NAVY} fontSize="12" fontWeight="600">{label}</text>
            <rect x={x} y={y} width={width} height="22" rx="3" fill={value >= 0 ? TEAL : RED} />
            <text x={value >= 0 ? x + width + 8 : x - 8} y={y + 16} textAnchor={value >= 0 ? "start" : "end"} fill={NAVY} fontSize="13" fontWeight="700">{value > 0 ? "+" : ""}{value}%</text>
          </g>;
        })}
        <text x="475" y="274" textAnchor="middle" fill={`${NAVY}aa`} fontSize="12">Projected employment change, 2025–35</text>
      </svg>
    </ChartFrame>
  );
}

export function GoldmanToolsComparison() {
  return (
    <section className="not-prose my-10 overflow-hidden rounded-2xl border border-[#0B1F33]/15 bg-[#F6F1E7] shadow-sm" aria-labelledby="goldman-tools-heading">
      <div className="border-b border-[#0B1F33]/15 px-5 py-5 sm:px-7">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0B1F33]/65">Comparison</p>
        <p id="goldman-tools-heading" className="mt-2 font-serif text-xl text-[#0B1F33] sm:text-2xl">Two Goldman tools, not one rollout</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[720px] w-full border-collapse text-left text-sm leading-6 text-[#0B1F33]">
          <thead className="bg-[#0B1F33] text-[#F6F1E7]">
            <tr><th scope="col" className="w-[23%] px-5 py-4 font-semibold"> </th><th scope="col" className="w-[38.5%] px-5 py-4 font-semibold">GS AI Assistant</th><th scope="col" className="w-[38.5%] px-5 py-4 font-semibold">Banker Copilot</th></tr>
          </thead>
          <tbody>
            <tr className="border-b border-[#0B1F33]/15"><th scope="row" className="bg-white/35 px-5 py-4 font-semibold">What it is</th><td className="px-5 py-4">Firmwide assistant</td><td className="px-5 py-4">Specialist tool for bankers, still being refined for broader use</td></tr>
            <tr className="border-b border-[#0B1F33]/15"><th scope="row" className="bg-white/35 px-5 py-4 font-semibold">Scale you can cite</th><td className="px-5 py-4">Weekly use by about 40,000 of 46,200 employees, millions of questions a month (TIME, 2026). Earlier: over one million prompts a month (Observer, 18 September 2025).</td><td className="px-5 py-4">No public user count and no public productivity figure.</td></tr>
            <tr><th scope="row" className="bg-white/35 px-5 py-4 font-semibold">What not to say</th><td className="px-5 py-4">That it runs without a person.</td><td className="px-5 py-4">That it is the same product, that it has left a pilot, or that a marketing team can copy it as “the bank already did this”.</td></tr>
          </tbody>
        </table>
      </div>
      <p className="border-t border-[#0B1F33]/15 bg-white/45 px-5 py-4 text-xs leading-5 text-[#0B1F33]/65 sm:px-7"><strong className="font-semibold text-[#0B1F33]">Sources:</strong> <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://time.com/collection/executives-of-the-year-tech-data/2026/marco-argenti/" target="_blank" rel="noopener noreferrer">TIME, 2026</a>; <a className="underline underline-offset-2 hover:text-[#0B1F33]" href="https://observer.com/2025/09/interview-marco-argenti-goldman-sachs-ai-strategy/" target="_blank" rel="noopener noreferrer">Observer, 18 September 2025</a>.</p>
    </section>
  );
}
